import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetClose } from "@/components/ui/sheet";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, User, CalendarIcon, Users, FileText, Phone, Plus, Pencil, Trash2, X } from "lucide-react";
import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { format, parse, isValid, differenceInYears } from "date-fns";
import { type Child, childSchema } from "@/schemas/registration-schema";
import type { ChildBase, AgeGroupBase } from "nautika-types";
import { AdultForm } from "@/features/dash/registrations/forms/adult-form";
import { cn } from "@/lib/utils";
import { SelectSearch } from "@/components/select-search";

interface ChildFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChildCreated: (child: ChildBase) => void;
  editingChild?: ChildBase; // enfant à consulter / éditer
  /** Mode initial : create | view | edit */
  mode?: "create" | "view" | "edit";
  /** Pré-remplir un groupe quand on vient de la dropdown d'ajout */
  defaultGroupId?: string;
  /** Afficher la section adultes de confiance (pour ajouts adhoc depuis le planning) */
  showTrustedAdults?: boolean;
}

export function ChildForm({ open, onOpenChange, onChildCreated, editingChild, mode = "create", defaultGroupId, showTrustedAdults = false }: ChildFormProps) {
  const queryClient = useQueryClient();

  // Only track a user override to switch to edit, scoped to the current child id.
  const [modeOverride, setModeOverride] = useState<{
    childId: string;
    mode: "edit";
  } | null>(null);

  // Compute the effective form mode from props and the optional user override.
  const baseMode: "create" | "view" | "edit" = editingChild ? mode : "create";
  const formMode: "create" | "view" | "edit" = modeOverride && editingChild && modeOverride.childId === editingChild.id ? modeOverride.mode : baseMode;

  // Charger les adultes de confiance de l'enfant (mode edit/view uniquement)
  const { data: childTrustedAdults } = api.useQuery("get", "/registrations/children/{id}/adults", {
    params: {
      path: { id: editingChild?.id || "" },
    },
    enabled: !!editingChild?.id && formMode !== "create" && open,
  });

  // State local pour gérer les adultes pendant la création
  const [localTrustedAdults, setLocalTrustedAdults] = useState<{ id: string; firstName: string; lastName: string; phone?: string }[]>([]);

  // UI optimiste pour ajout/suppression d'adultes
  const [optimisticRemovedAdults, setOptimisticRemovedAdults] = useState<Set<string>>(new Set());
  const [recentlyAddedAdults, setRecentlyAddedAdults] = useState<Set<string>>(new Set());
  const [optimisticAddedAdults, setOptimisticAddedAdults] = useState<{ id: string; firstName: string; lastName: string; phone?: string }[]>([]);
  const [optimisticEditedAdults, setOptimisticEditedAdults] = useState<Record<string, { firstName: string; lastName: string; phone?: string }>>({});
  // Ordre stable pendant l'ouverture de la sheet: initial alphabétique, puis ajouts en bas dans l'ordre d'insertion
  const [stableAdultOrder, setStableAdultOrder] = useState<string[]>([]);
  const [adultAdHocOrder, setAdultAdHocOrder] = useState<Record<string, number>>({});
  const adultAdHocCounter = useRef(0);

  // Fusionner les adultes selon le mode avec UI optimiste
  const trustedAdults = useMemo(() => {
    if (formMode === "create") {
      // En mode création, uniquement l'état local
      const base = localTrustedAdults.map((a) => ({ ...a, ...(optimisticEditedAdults[a.id] || {}) }));
      // Tri alphabétique par défaut en mode création
      return base.slice().sort((a, b) => {
        const ln = a.lastName.localeCompare(b.lastName, "fr", { sensitivity: "base" });
        if (ln !== 0) return ln;
        return a.firstName.localeCompare(b.firstName, "fr", { sensitivity: "base" });
      });
    }

    const serverAdults = childTrustedAdults?.adults.map((a) => ({ id: a.id, firstName: a.firstName, lastName: a.lastName, phone: a.phone })) || [];

    // Filtrer les adultes supprimés de façon optimiste
    const filtered = serverAdults.filter((a) => !optimisticRemovedAdults.has(a.id));

    // Fusionner les ajouts optimistes (éviter doublons)
    const byId = new Map<string, { id: string; firstName: string; lastName: string; phone?: string }>();
    filtered.forEach((a) => byId.set(a.id, a));
    optimisticAddedAdults.forEach((a) => {
      if (!optimisticRemovedAdults.has(a.id)) {
        byId.set(a.id, a);
      }
    });

    // Appliquer les éditions optimistes par-dessus
    const merged = Array.from(byId.values()).map((a) => ({ ...a, ...(optimisticEditedAdults[a.id] || {}) }));

    // Ordonner selon stableAdultOrder; les nouveaux (pas dans stable) restent en bas selon ordre d'insertion
    const order = stableAdultOrder;
    if (order.length === 0) {
      // Si pas encore d'ordre stable, trier alphabétiquement (seulement à l'ouverture avant setStableAdultOrder)
      return merged.slice().sort((a, b) => {
        const ln = a.lastName.localeCompare(b.lastName, "fr", { sensitivity: "base" });
        if (ln !== 0) return ln;
        return a.firstName.localeCompare(b.firstName, "fr", { sensitivity: "base" });
      });
    }

    const withOrder = merged.slice().sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      if (ai === -1 && bi === -1) {
        // Les deux sont des ajouts récents: garder l'ordre d'insertion ad hoc
        const av = adultAdHocOrder[a.id] ?? Number.MAX_SAFE_INTEGER;
        const bv = adultAdHocOrder[b.id] ?? Number.MAX_SAFE_INTEGER;
        return av - bv;
      }
      if (ai === -1) return 1; // élément inconnu va à la fin
      if (bi === -1) return -1;
      return ai - bi;
    });

    return withOrder;
  }, [
    formMode,
    localTrustedAdults,
    childTrustedAdults,
    optimisticRemovedAdults,
    optimisticAddedAdults,
    optimisticEditedAdults,
    stableAdultOrder,
    adultAdHocOrder,
  ]);

  // Réinitialiser l'ordre et les états optimistes quand la sheet se ferme via handler
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStableAdultOrder([]);
      setAdultAdHocOrder({});
      adultAdHocCounter.current = 0;
      setOptimisticAddedAdults([]);
      setOptimisticRemovedAdults(new Set());
      setRecentlyAddedAdults(new Set());
      setModeOverride(null);
    }
    onOpenChange(nextOpen);
  };

  // État pour la sheet AdultForm
  const [adultFormOpen, setAdultFormOpen] = useState(false);
  const [adultFormMode, setAdultFormMode] = useState<"create" | "view" | "edit">("create");
  const [viewingAdult, setViewingAdult] = useState<{ id: string; firstName: string; lastName: string; phone?: string } | undefined>(undefined);

  const form = useForm<Child>({
    resolver: zodResolver(childSchema),
    reValidateMode: "onBlur",
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      birthDate: "",
      gender: undefined,
      notes: "",
      leaveAlone: false,
      imageRights: false,
      groupId: undefined,
    },
  });

  // Récupérer les groupes d'âge
  const { data: ageGroupsResponse } = api.useQuery("get", "/club/age-groups", {});
  const ageGroups = useMemo(() => (ageGroupsResponse || []) as AgeGroupBase[], [ageGroupsResponse]);

  // Format date input with automatic "/" insertion
  const formatDateInput = useCallback((value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Add slashes automatically
    if (digits.length >= 2) {
      if (digits.length >= 4) {
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
      }
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    return digits;
  }, []);

  // Parse date string to ISO format for API
  const parseDateToISO = useCallback((dateStr: string): string => {
    if (!dateStr || dateStr.length < 10) return "";

    try {
      const [day, month, year] = dateStr.split("/");
      if (!day || !month || !year) return "";
      const parsed = parse(dateStr, "dd/MM/yyyy", new Date());
      return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : "";
    } catch {
      return "";
    }
  }, []);

  // Convert ISO date to display format (DD/MM/YYYY)
  const formatDateDisplay = useCallback((isoDate: string | number | Date): string => {
    if (!isoDate) return "";
    try {
      // Handle different input types
      let dateStr: string;
      if (typeof isoDate === "string") {
        dateStr = isoDate;
      } else if (typeof isoDate === "number") {
        // Convert timestamp to ISO string
        dateStr = new Date(isoDate).toISOString();
      } else {
        // Date object
        dateStr = isoDate.toISOString();
      }

      const parsed = parse(dateStr, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", new Date());
      return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : "";
    } catch {
      return "";
    }
  }, []);

  // Calculer l'âge à partir de la date de naissance
  const calculateAge = useCallback((birthDate: string): number => {
    if (!birthDate || birthDate.length < 10) return 0;

    try {
      const [day, month, year] = birthDate.split("/");
      if (!day || !month || !year) return 0;
      const parsed = parse(birthDate, "dd/MM/yyyy", new Date());
      return isValid(parsed) ? differenceInYears(new Date(), parsed) : 0;
    } catch {
      return 0;
    }
  }, []);

  // Trouver le groupe d'âge approprié pour un âge donné
  const findAgeGroupForAge = useCallback(
    (age: number): string => {
      const appropriateGroup = ageGroups.find((group: AgeGroupBase) => age >= group.minAge && age <= group.maxAge);
      return appropriateGroup ? appropriateGroup.id : "";
    },
    [ageGroups]
  );

  // Effet pour suggérer automatiquement le groupe d'âge quand la date de naissance change
  // Uniquement en mode création (pas quand on édite un enfant existant)
  const watchedBirthDate = useWatch({ control: form.control, name: "birthDate" });
  useEffect(() => {
    // Ne jamais réassigner si on édite un enfant existant
    if (editingChild) return;
    if (formMode !== "create") return;
    if (!watchedBirthDate) return;

    const age = calculateAge(watchedBirthDate);
    if (age <= 0) return;

    const suggestedGroupId = findAgeGroupForAge(age);
    if (suggestedGroupId) {
      form.setValue("groupId", suggestedGroupId);
    }
  }, [watchedBirthDate, calculateAge, findAgeGroupForAge, form, formMode, editingChild]);

  // Réinitialiser le formulaire quand editingChild change ou que le panneau s'ouvre
  useEffect(() => {
    if (open) {
      if (editingChild) {
        form.reset({
          firstName: editingChild.firstName,
          lastName: editingChild.lastName,
          birthDate: formatDateDisplay(editingChild.birthDate),
          gender: editingChild.gender as "M" | "F" | undefined,
          notes: editingChild.notes,
          leaveAlone: editingChild.leaveAlone,
          imageRights: editingChild.imageRights,
          groupId: editingChild.groupId,
        });
      } else {
        form.reset({
          firstName: "",
          lastName: "",
          birthDate: "",
          gender: undefined,
          notes: "",
          leaveAlone: false,
          imageRights: false,
          groupId: defaultGroupId || undefined,
        });
      }
    }
  }, [editingChild, open, form, formatDateDisplay, defaultGroupId]);

  const createMutation = api.useMutation("post", "/registrations/children/", {
    onSuccess: (response) => {
      onChildCreated(response as ChildBase);
      form.reset();
      onOpenChange(false);
      toast.success("Enfant créé avec succès", { id: "child-created" });
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la création");
    },
  });

  const updateMutation = api.useMutation("put", "/registrations/children/{id}", {
    onSuccess: (response) => {
      onChildCreated(response as ChildBase);
      form.reset();
      onOpenChange(false);
      toast.success("Enfant modifié avec succès");
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la modification");
    },
  });

  const onSubmit = (data: Child) => {
    // Convertir la date d'affichage en format ISO pour l'API
    const apiData = {
      ...data,
      birthDate: parseDateToISO(data.birthDate),
      gender: data.gender || undefined,
      groupId: data.groupId || undefined,
    };
    if (formMode === "view") return; // no-op
    if (editingChild && formMode === "edit") {
      updateMutation.mutate({
        params: { path: { id: editingChild.id } },
        body: apiData,
      });
    } else {
      createMutation.mutate({ body: apiData });
    }
  };

  const isReadOnly = formMode === "view" || createMutation.isPending || updateMutation.isPending;

  // Charger tous les adultes de l'organisation
  const { data: allAdultsData } = api.useQuery("get", "/registrations/adults/", {
    enabled: open,
  });

  const availableAdults = useMemo(
    () =>
      Array.isArray(allAdultsData)
        ? allAdultsData.map((a: { id: string; firstName: string; lastName: string; phone?: string }) => ({
            id: a.id,
            firstName: a.firstName,
            lastName: a.lastName,
            phone: a.phone,
          }))
        : [],
    [allAdultsData]
  );

  const [adultComboOpen, setAdultComboOpen] = useState(false);
  const [adultSearchTerm, setAdultSearchTerm] = useState("");

  const filteredAvailableAdults = useMemo(() => {
    // Appliquer d'abord les éditions optimistes sur la liste globale
    const withEdits = availableAdults.map((a) => ({ ...a, ...(optimisticEditedAdults[a.id] || {}) }));
    if (!adultSearchTerm.trim()) return withEdits;
    const term = adultSearchTerm.toLowerCase();
    return withEdits.filter((a: { firstName: string; lastName: string; phone?: string }) =>
      `${a.firstName} ${a.lastName} ${a.phone || ""}`.toLowerCase().includes(term)
    );
  }, [adultSearchTerm, availableAdults, optimisticEditedAdults]);

  const availableAdultsNotSelected = useMemo(() => {
    const selectedIds = new Set(trustedAdults.map((a) => a.id));
    return filteredAvailableAdults.filter((a: { id: string }) => !selectedIds.has(a.id));
  }, [filteredAvailableAdults, trustedAdults]);

  // Mutations pour gérer les adultes de confiance
  const linkAdultMutation = api.useMutation("post", "/registrations/children/{id}/adults", {
    onSuccess: () => {
      // Invalider le cache pour recharger les adultes de confiance
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/children/{id}/adults"] });
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/adults/"] });
      // Pas de toast ici, géré dans handleAddTrustedAdult
    },
    onError: (error) => {
      toast.error("Erreur lors de l'ajout de l'adulte", { description: error.message });
    },
  });

  const unlinkAdultMutation = api.useMutation("delete", "/registrations/children/{id}/adults/{adultId}", {
    onSuccess: () => {
      // Invalider le cache pour recharger les adultes de confiance
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/children/{id}/adults"] });
      // Pas de toast ici, géré dans handleRemoveTrustedAdult
    },
    onError: (error) => {
      toast.error("Erreur lors du retrait de l'adulte", { description: error.message });
    },
  });

  // Ajouter adulte à un enfant (création = local, edit = API avec UI optimiste)
  const handleAddTrustedAdult = useCallback(
    (adult: { id: string; firstName: string; lastName: string; phone?: string }) => {
      if (formMode === "create") {
        // En mode création, stocker localement
        setLocalTrustedAdults((prev) => (prev.find((a) => a.id === adult.id) ? prev : [...prev, adult]));
        setAdultComboOpen(false);
        toast.success(`${adult.firstName} ${adult.lastName} ajouté`, { id: `adult-added-${adult.id}` });
        return;
      }

      if (!editingChild) return;

      // UI optimiste : ajouter immédiatement dans la liste
      setOptimisticAddedAdults((prev) => (prev.find((a) => a.id === adult.id) ? prev : [...prev, adult]));
      // Maintenir l'ordre: l'adulte ajouté va en bas et y reste jusqu'à fermeture de la sheet
      setStableAdultOrder((prev) => (prev.includes(adult.id) ? prev : [...prev, adult.id]));
      setAdultAdHocOrder((prev) => {
        if (prev[adult.id] !== undefined) return prev;
        const nextIndex = adultAdHocCounter.current + 1;
        adultAdHocCounter.current = nextIndex;
        return { ...prev, [adult.id]: nextIndex };
      });
      setRecentlyAddedAdults((prev) => new Set(prev).add(adult.id));
      setAdultComboOpen(false);
      toast.success(`${adult.firstName} ${adult.lastName} ajouté`, { id: `adult-added-${adult.id}` });

      // Retirer le marquage "nouveau" après 3s
      setTimeout(() => {
        setRecentlyAddedAdults((prev) => {
          const next = new Set(prev);
          next.delete(adult.id);
          return next;
        });
      }, 3000);

      // Appeler l'API; rollback en cas d'erreur
      linkAdultMutation.mutate(
        {
          params: {
            path: { id: editingChild.id },
          },
          body: { adultId: adult.id },
        },
        {
          onError: () => {
            // Rollback visuel
            setOptimisticAddedAdults((prev) => prev.filter((a) => a.id !== adult.id));
            setRecentlyAddedAdults((prev) => {
              const next = new Set(prev);
              next.delete(adult.id);
              return next;
            });
          },
        }
      );
    },
    [formMode, editingChild, linkAdultMutation]
  );

  // Retirer adulte (création = local, edit = API avec UI optimiste)
  const handleRemoveTrustedAdult = useCallback(
    (adultId: string, adultName: string) => {
      if (formMode === "create") {
        setLocalTrustedAdults((prev) => prev.filter((a) => a.id !== adultId));
        toast.success(`${adultName} retiré`, { id: `adult-removed-${adultId}` });
        return;
      }

      if (!editingChild) return;

      // Conserver si l'adulte était ajouté de façon optimiste pour rollback
      const wasOptimisticallyAdded = optimisticAddedAdults.some((a) => a.id === adultId);

      // UI optimiste : retirer immédiatement
      setOptimisticRemovedAdults((prev) => new Set(prev).add(adultId));
      setOptimisticAddedAdults((prev) => prev.filter((a) => a.id !== adultId));
      setStableAdultOrder((prev) => prev.filter((id) => id !== adultId));
      setAdultAdHocOrder((prev) => {
        if (!(adultId in prev)) return prev;
        const clone = { ...prev };
        delete clone[adultId];
        return clone;
      });
      toast.success(`${adultName} retiré`, { id: `adult-removed-${adultId}` });

      unlinkAdultMutation.mutate(
        {
          params: {
            path: { id: editingChild.id, adultId },
          },
        },
        {
          onError: () => {
            // Rollback en cas d'erreur
            setOptimisticRemovedAdults((prev) => {
              const next = new Set(prev);
              next.delete(adultId);
              return next;
            });
            if (wasOptimisticallyAdded) {
              const adult = availableAdults.find((a) => a.id === adultId);
              if (adult) setOptimisticAddedAdults((prev) => [...prev, adult]);
            }
          },
          onSuccess: () => {
            // Assurer cohérence via invalidation
            queryClient.invalidateQueries({ queryKey: ["get", "/registrations/children/{id}/adults"] });
          },
        }
      );
    },
    [formMode, editingChild, unlinkAdultMutation, optimisticAddedAdults, availableAdults, queryClient]
  );

  const handleOpenCreateAdult = useCallback(() => {
    setAdultFormMode("create");
    setViewingAdult(undefined);
    setAdultFormOpen(true);
  }, []);

  // Utilitaire d'affichage adulte
  const renderAdultChip = (adult: { id: string; firstName: string; lastName: string; phone?: string }, isNew = false) => (
    <div
      key={adult.id}
      className={cn(
        "flex items-center gap-1 rounded-md border p-1.5 text-xs transition-all",
        isNew && "ring-1 ring-blue-300/60 animate-in fade-in-0 zoom-in-95"
      )}
    >
      <div
        className="flex flex-1 cursor-pointer items-center gap-1"
        onClick={() => {
          setViewingAdult(adult);
          setAdultFormMode("view");
          setAdultFormOpen(true);
        }}
      >
        {/* Name pill */}
        <div className="inline-flex h-7 items-center gap-2 rounded-lg bg-indigo-600 px-3 text-sm font-medium text-white shadow-sm">
          <span className="max-w-48 truncate">
            {adult.firstName} {adult.lastName}
          </span>
        </div>

        {/* Phone pill (optional) */}
        {adult.phone && (
          <div className="inline-flex h-7 items-center gap-2 rounded-lg bg-indigo-100 px-3 text-xs text-indigo-800">
            <Phone className="h-4 w-4" />
            <span className="max-w-32 truncate">{adult.phone}</span>
          </div>
        )}
      </div>

      {/* Remove button */}
      {!isReadOnly && (
        <Button
          size="icon"
          variant="ghost"
          type="button"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={() => handleRemoveTrustedAdult(adult.id, `${adult.firstName} ${adult.lastName}`)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="h-screen max-w-md! overflow-y-auto [&>button]:hidden">
        <SheetHeader className="sticky top-0 z-2 border-b bg-white shadow-xs">
          <div className="flex items-center justify-between gap-4">
            <SheetTitle>
              {formMode === "view" && editingChild && `Fiche ${editingChild.firstName} ${editingChild.lastName}`}
              {formMode === "edit" && editingChild && `Modifier ${editingChild.firstName}`}
              {formMode === "create" && "Nouvel enfant"}
            </SheetTitle>
            <div className="flex items-center gap-2">
              {formMode === "view" && editingChild && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setModeOverride({ childId: editingChild.id, mode: "edit" })}>
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Modifier
                  </Button>
                  <SheetClose asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Fermer">
                      <X className="h-4 w-4" />
                    </Button>
                  </SheetClose>
                </>
              )}
              {formMode === "edit" && editingChild && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    // Reset values to original and revert to view mode if override
                    form.reset({
                      firstName: editingChild.firstName,
                      lastName: editingChild.lastName,
                      birthDate: formatDateDisplay(editingChild.birthDate),
                      gender: editingChild.gender as "M" | "F" | undefined,
                      notes: editingChild.notes,
                      leaveAlone: editingChild.leaveAlone,
                      imageRights: editingChild.imageRights,
                      groupId: editingChild.groupId,
                    });
                    if (modeOverride && modeOverride.childId === editingChild.id) {
                      setModeOverride(null);
                    } else {
                      onOpenChange(false);
                    }
                  }}
                >
                  <X className="mr-2 h-3.5 w-3.5" /> Annuler
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4 px-8">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <User className="mr-2 inline h-4 w-4" />
                    Prénom <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Prénom" {...field} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <User className="mr-2 inline h-4 w-4" />
                    Nom <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Nom" {...field} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="birthDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <CalendarIcon className="mr-2 inline h-4 w-4" />
                    Date de naissance <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="JJ/MM/AAAA"
                      maxLength={10}
                      {...field}
                      onChange={(e) => {
                        const formatted = formatDateInput(e.target.value);
                        field.onChange(formatted);
                      }}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <User className="mr-2 inline h-4 w-4" />
                    Genre <span className="text-red-500">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isReadOnly}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un genre" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="M">Masculin</SelectItem>
                      <SelectItem value="F">Féminin</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="groupId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Users className="mr-2 inline h-4 w-4" />
                    Groupe d'âge <span className="text-red-500">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isReadOnly}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un groupe" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ageGroups.map((group: AgeGroupBase) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name} ({group.minAge}-{group.maxAge} ans)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FileText className="mr-2 inline h-4 w-4" />
                    Notes
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Allergies, maladies, comportements particuliers, médicaments..." rows={3} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="leaveAlone"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    "flex flex-row items-start rounded-lg border p-4 select-none",
                    isReadOnly ? "cursor-not-allowed **:cursor-not-allowed!" : "cursor-pointer **:cursor-pointer!"
                  )}
                  onClick={() => {
                    if (isReadOnly) return;
                    field.onChange(!field.value);
                  }}
                  tabIndex={isReadOnly ? -1 : 0}
                  role={isReadOnly ? undefined : "button"}
                  aria-pressed={isReadOnly ? undefined : field.value}
                  aria-disabled={isReadOnly}
                  onKeyDown={(e) => {
                    if (isReadOnly) return;
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      field.onChange(!field.value);
                    }
                  }}
                >
                  <FormControl>
                    <div onClick={(e) => e.stopPropagation()} className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="h-full w-full data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500"
                        disabled={isReadOnly}
                      />
                    </div>
                  </FormControl>
                  <div className="ml-3 flex flex-1 flex-col gap-y-0.5">
                    <FormLabel className="text-base">Peut partir seul</FormLabel>
                    <p className="text-sm text-muted-foreground">L'enfant est autorisé à quitter le centre sans être accompagné</p>
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="imageRights"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    "flex flex-row items-start rounded-lg border p-4 select-none",
                    isReadOnly ? "cursor-not-allowed **:cursor-not-allowed!" : "cursor-pointer **:cursor-pointer!"
                  )}
                  onClick={() => {
                    if (isReadOnly) return;
                    field.onChange(!field.value);
                  }}
                  tabIndex={isReadOnly ? -1 : 0}
                  role={isReadOnly ? undefined : "button"}
                  aria-pressed={isReadOnly ? undefined : field.value}
                  aria-disabled={isReadOnly}
                  onKeyDown={(e) => {
                    if (isReadOnly) return;
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      field.onChange(!field.value);
                    }
                  }}
                >
                  <FormControl>
                    <div onClick={(e) => e.stopPropagation()} className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="h-full w-full data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500"
                        disabled={isReadOnly}
                      />
                    </div>
                  </FormControl>
                  <div className="ml-3 flex flex-col gap-y-0.5">
                    <FormLabel className="text-base">Droit à l'image accordé</FormLabel>
                    <p className="text-sm text-muted-foreground">Autorisation de photographier/filmer l'enfant pour les activités</p>
                  </div>
                </FormItem>
              )}
            />

            {/* Section Adultes de confiance - affichée uniquement si showTrustedAdults est true */}
            {showTrustedAdults && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Adultes de confiances</h4>
                  {formMode !== "view" && (
                    <Button size="sm" variant="ghost" type="button" className="h-7 px-2 text-xs" onClick={handleOpenCreateAdult}>
                      <Plus className="mr-1 h-3.5 w-3.5" /> Créer un adulte
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {trustedAdults.map((a) => {
                    const isNew = recentlyAddedAdults.has(a.id);
                    return renderAdultChip(a, isNew);
                  })}
                  {/* Combobox pour ajouter un adulte (avec bouton "Créer" en haut) */}
                  {formMode !== "view" && (
                    <SelectSearch
                      items={availableAdultsNotSelected}
                      onItemSelect={(adult) => handleAddTrustedAdult(adult)}
                      onCreateNew={handleOpenCreateAdult}
                      filterFunction={(items, search) => {
                        const term = search.trim().toLowerCase();
                        if (!term) return items;
                        return items.filter((a) => `${a.firstName} ${a.lastName} ${a.phone || ""}`.toLowerCase().includes(term));
                      }}
                      renderItem={(adult) => (
                        <div className="flex flex-col text-left">
                          <span className="text-sm font-medium">
                            {adult.firstName} {adult.lastName}
                          </span>
                          {adult.phone && <span className="text-xs text-muted-foreground">{adult.phone}</span>}
                        </div>
                      )}
                      renderTrigger={() => (
                        <div className="group relative w-full">
                          <button
                            type="button"
                            className="flex w-full items-center justify-start rounded-md border border-dashed border-muted-foreground/30 p-1.5 text-left text-xs text-muted-foreground transition hover:bg-muted/40 md:p-2"
                          >
                            <Plus className="mr-2 h-3.5 w-3.5" /> Ajouter un adulte de confiance
                          </button>
                        </div>
                      )}
                      placeholder="Rechercher un adulte..."
                      createNewText="Créer un adulte"
                      emptyMessage="Aucun adulte trouvé"
                      width="w-[350px]"
                      popoverAlign="start"
                      showCreateButton={true}
                      closeOnSelect={true}
                      isMultiSelect={false}
                      modal={true}
                      open={adultComboOpen}
                      onOpenChange={(o) => {
                        setAdultComboOpen(o);
                        if (!o) setAdultSearchTerm("");
                      }}
                    />
                  )}
                  {trustedAdults.length === 0 && formMode === "view" && <p className="text-xs text-muted-foreground italic">Aucun adulte renseigné</p>}
                </div>
              </div>
            )}
            <SheetFooter className="px-8">
              {formMode !== "view" && (
                <>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {createMutation.isPending || updateMutation.isPending
                      ? editingChild
                        ? "Modification..."
                        : "Création..."
                      : editingChild
                        ? "Enregistrer les modifications"
                        : "Ajouter cet enfant"}
                  </Button>
                  <SheetClose asChild>
                    <Button variant="outline">Annuler</Button>
                  </SheetClose>
                </>
              )}
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>

      {/* Form adulte imbriqué */}
      <AdultForm
        open={adultFormOpen}
        onOpenChange={setAdultFormOpen}
        mode={adultFormMode}
        editingAdult={
          viewingAdult
            ? {
                id: viewingAdult.id,
                firstName: viewingAdult.firstName,
                lastName: viewingAdult.lastName,
                email: "",
                phone: viewingAdult.phone || "",
                organizationId: "",
                createdAt: "",
                updatedAt: "",
              }
            : undefined
        }
        onAdultCreated={(adult) => {
          // Ajouter l'adulte créé à la liste disponible
          if (formMode === "create") {
            // En mode création, ajouter localement
            setLocalTrustedAdults((prev) =>
              prev.find((a) => a.id === adult.id) ? prev : [...prev, { id: adult.id, firstName: adult.firstName, lastName: adult.lastName, phone: adult.phone }]
            );
          } else if (editingChild && adultFormMode === "create") {
            // En mode édition/visualisation, lier l'adulte créé à l'enfant
            handleAddTrustedAdult(adult);
          } else {
            // Edition d'un adulte existant: appliquer une maj optimiste immédiate
            setOptimisticEditedAdults((prev) => ({
              ...prev,
              [adult.id]: { firstName: adult.firstName, lastName: adult.lastName, phone: adult.phone },
            }));
            // Mettre à jour la fiche en cours de consultation si c'est le même adulte
            setViewingAdult((curr) =>
              curr && curr.id === adult.id ? { ...curr, firstName: adult.firstName, lastName: adult.lastName, phone: adult.phone } : curr
            );
            // Invalidation pour récupérer les données fraîches en arrière-plan
            queryClient.invalidateQueries({ queryKey: ["get", "/registrations/adults/"] });
            if (editingChild) {
              queryClient.invalidateQueries({ queryKey: ["get", "/registrations/children/{id}/adults"] });
            }
          }
          setAdultFormOpen(false);
          toast.success(adultFormMode === "create" ? "Adulte créé avec succès" : "Adulte modifié avec succès");
        }}
      />
    </Sheet>
  );
}
